import * as React from 'react';
import { Check, Minus } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Checkbox as CheckboxPrimitive } from 'radix-ui';
import { cn } from '../../lib/utils';

/**
 * Nava Checkbox — matches the Figma Checkbox spec.
 * Square box, 3 sizes, checked/indeterminate/unchecked. Checked fills dark
 * (bg-inverse) with a white check; indeterminate shows a white dash. Built on the
 * Radix Checkbox primitive (role="checkbox", aria-checked incl. "mixed", keyboard).
 * Box dims are rem (px in comments) from the Figma frames, so they scale.
 */
const boxVariants = cva(
  'peer inline-flex shrink-0 items-center justify-center rounded-[0.1875rem] border transition-colors cursor-pointer focus-ring disabled:cursor-not-allowed disabled:opacity-50 bg-canvas border-border hover:border-border-strong data-[state=checked]:bg-inverse data-[state=checked]:border-transparent data-[state=indeterminate]:bg-inverse data-[state=indeterminate]:border-transparent',
  {
    variants: {
      size: {
        sm: 'size-[0.875rem]', // 14px
        md: 'size-[1rem]', // 16px
        lg: 'size-[1.25rem]', // 20px
      },
    },
    defaultVariants: { size: 'md' },
  },
);

const iconSize: Record<NonNullable<VariantProps<typeof boxVariants>['size']>, string> = {
  sm: 'size-[0.625rem]', // 10px
  md: 'size-3', // 12px
  lg: 'size-[0.875rem]', // 14px
};

export interface CheckboxProps
  extends React.ComponentProps<typeof CheckboxPrimitive.Root>, VariantProps<typeof boxVariants> {
  /** Optional label rendered beside the box and wired via htmlFor. */
  label?: React.ReactNode;
}

/** Checkbox — 3 sizes, checked / indeterminate / unchecked, optional label. Built on Radix. */
export function Checkbox({ size = 'md', label, className, id, checked, ...rest }: CheckboxProps) {
  const generatedId = React.useId();
  const boxId = id ?? generatedId;
  const resolvedSize = size ?? 'md';

  const control = (
    <CheckboxPrimitive.Root
      id={boxId}
      checked={checked}
      className={cn(boxVariants({ size: resolvedSize }), className)}
      {...rest}
    >
      <CheckboxPrimitive.Indicator className="text-inverse-fg">
        {checked === 'indeterminate' ? (
          <Minus className={iconSize[resolvedSize]} strokeWidth={3} />
        ) : (
          <Check className={iconSize[resolvedSize]} strokeWidth={3} />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );

  if (!label) return control;

  return (
    <label htmlFor={boxId} className="flex items-center gap-space-8">
      {control}
      <span className="type-text-sm cursor-pointer">{label}</span>
    </label>
  );
}
